import React, { useEffect, useState } from "react";
import { api, errMsg, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Card, Btn, Field, Input, Select, Empty } from "@/components/ui-kit";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Plus,
  Pencil,
  FileText,
  Building2,
  IndianRupee,
  WalletCards,
  ReceiptText,
  Download,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  code: "",
  name: "",
  legal_name: "",
  gstin: "",
  pan: "",
  address: "",
  state: "",
  contact_person: "",
  phone: "",
  email: "",
  active: true,
  esi_applicable: true,

  // Default commercial terms
  margin_type: "none",
  margin_fixed: "0",
  margin_percent: "0",
  gst: "18",
  payment_terms_days: "30",
};

const MARGIN_TYPES = [
  { value: "none", label: "No Margin" },
  { value: "fixed", label: "Fixed Amount" },
  { value: "percentage", label: "Percentage" },
  { value: "fixed_plus_percentage", label: "Fixed + Percentage" },
];

const marginLabel = (c) => {
  const type = c?.margin_type || "none";

  if (type === "fixed") {
    return `₹${Number(c.margin_fixed || 0).toLocaleString("en-IN")}`;
  }

  if (type === "percentage") {
    return `${c.margin_percent || 0}%`;
  }

  if (type === "fixed_plus_percentage") {
    return `₹${Number(c.margin_fixed || 0).toLocaleString("en-IN")} + ${c.margin_percent || 0}%`;
  }

  return "None";
};

export default function Clients() {
  const { isSuper } = useAuth();

  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [contractsFor, setContractsFor] = useState(null);
  const [ledgerFor, setLedgerFor] = useState(null);
  const [clientTab, setClientTab] = useState("basic");

  const load = () =>
    api.get("/clients").then((r) => setClients(r.data));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(EMPTY);
    setEditId(null);
    setClientTab("basic");
    setOpen(true);
  };

  const openEdit = (c) => {
    setForm({
      ...EMPTY,
      ...c,
      margin_type: c.margin_type || "none",
      margin_fixed: c.margin_fixed ?? "0",
      margin_percent: c.margin_percent ?? "0",
      gst: c.gst ?? "18",
      payment_terms_days: c.payment_terms_days ?? "30",
    });

    setEditId(c.id);
    setClientTab("basic");
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editId) {
        await api.put(`/clients/${editId}`, form);
      } else {
        await api.post("/clients", form);
      }

      toast.success("Client saved");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const set = (k) => (e) =>
    setForm({
      ...form,
      [k]: e.target.value,
    });

  return (
    <div>
      <PageHeader
        title="Clients"
        subtitle="Institutions Nakshatra supplies workforce to"
        actions={
          isSuper && (
            <Btn
              onClick={openNew}
              data-testid="add-client-btn"
            >
              <Plus size={16} />
              Add Client
            </Btn>
          )
        }
      />

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          {clients.length ? (
            <table
              className="nk-table"
              data-testid="clients-table"
            >
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>GSTIN</th>
                  <th>State</th>
                  <th>Margin</th>
                  <th>GST %</th>
                  <th>ESI</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {clients.map((c) => (
                  <tr
                    key={c.id}
                    data-testid={`client-row-${c.code}`}
                  >
                    <td className="font-semibold text-slate-800">
                      {c.code}
                    </td>

                    <td>{c.name}</td>

                    <td className="text-slate-500">
                      {c.gstin || "—"}
                    </td>

                    <td>{c.state || "—"}</td>

                    <td className="whitespace-nowrap">
                      {marginLabel(c)}
                    </td>

                    <td className="nk-num">
                      {c.gst ?? "18"}
                    </td>

                    <td>{c.esi_applicable === false ? "No" : "Yes"}</td>
                    <td>
                      <StatusBadge
                        status={
                          c.active ? "active" : "inactive"
                        }
                      />
                    </td>

                    <td className="text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <Btn
                          variant="ghost"
                          onClick={() =>
                            setContractsFor(c)
                          }
                          data-testid={`contracts-btn-${c.code}`}
                        >
                          <FileText size={14} />
                          Contracts
                        </Btn>

                        <Btn
                          variant="ghost"
                          onClick={() =>
                            setLedgerFor(c)
                          }
                          title="Client Ledger"
                        >
                          <WalletCards size={14} />
                          Ledger
                        </Btn>

                        {isSuper && (
                          <Btn
                            variant="ghost"
                            onClick={() => openEdit(c)}
                            data-testid={`edit-client-${c.code}`}
                          >
                            <Pencil size={14} />
                          </Btn>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Empty
              title="No clients yet"
              hint="Add your first client to get started."
            />
          )}
        </div>
      </Card>

      {/* CLIENT ADD / EDIT */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editId ? "Edit Client" : "Add Client"}
            </DialogTitle>
          </DialogHeader>

          <div className="flex gap-2 border-b border-slate-200 mb-4">
            <button
              type="button"
              onClick={() => setClientTab("basic")}
              className={`px-4 py-2 text-sm font-semibold border-b-2 ${
                clientTab === "basic"
                  ? "border-blue-600 text-blue-700"
                  : "border-transparent text-slate-500"
              }`}
            >
              <span className="inline-flex items-center gap-2">
                <Building2 size={15} />
                Basic Details
              </span>
            </button>

            <button
              type="button"
              onClick={() => setClientTab("commercial")}
              className={`px-4 py-2 text-sm font-semibold border-b-2 ${
                clientTab === "commercial"
                  ? "border-blue-600 text-blue-700"
                  : "border-transparent text-slate-500"
              }`}
            >
              <span className="inline-flex items-center gap-2">
                <IndianRupee size={15} />
                Commercial Terms
              </span>
            </button>
          </div>

          {clientTab === "basic" && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Client Code">
                <Input
                  value={form.code}
                  onChange={set("code")}
                  data-testid="client-code"
                />
              </Field>

              <Field label="Client Name">
                <Input
                  value={form.name}
                  onChange={set("name")}
                  data-testid="client-name"
                />
              </Field>

              <Field label="Legal Name">
                <Input
                  value={form.legal_name}
                  onChange={set("legal_name")}
                />
              </Field>

              <Field label="GSTIN">
                <Input
                  value={form.gstin}
                  onChange={set("gstin")}
                />
              </Field>

              <Field label="Company ESI Applicable">
                <Select value={form.esi_applicable === false ? "false" : "true"} onChange={(e) => setForm({ ...form, esi_applicable: e.target.value === "true" })} data-testid="client-esi-applicable">
                  <option value="true">Yes — use employee eligibility</option>
                  <option value="false">No — employee and employer ESI = 0</option>
                </Select>
                <p className="text-xs text-slate-500 mt-1">Applies on the next calculation. Existing finalized payroll remains unchanged.</p>
              </Field>
              <Field label="PAN">
                <Input
                  value={form.pan}
                  onChange={set("pan")}
                />
              </Field>

              <Field label="State">
                <Input
                  value={form.state}
                  onChange={set("state")}
                />
              </Field>

              <Field label="Address">
                <Input
                  value={form.address}
                  onChange={set("address")}
                />
              </Field>

              <Field label="Contact Person">
                <Input
                  value={form.contact_person}
                  onChange={set("contact_person")}
                />
              </Field>

              <Field label="Phone">
                <Input
                  value={form.phone}
                  onChange={set("phone")}
                />
              </Field>

              <Field label="Email">
                <Input
                  value={form.email}
                  onChange={set("email")}
                />
              </Field>

              <Field label="Status">
                <Select
                  value={
                    form.active ? "true" : "false"
                  }
                  onChange={(e) =>
                    setForm({
                      ...form,
                      active:
                        e.target.value === "true",
                    })
                  }
                >
                  <option value="true">
                    Active
                  </option>
                  <option value="false">
                    Inactive
                  </option>
                </Select>
              </Field>
            </div>
          )}

          {clientTab === "commercial" && (
            <div>
              <div className="mb-4 rounded-md border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-slate-600">
                These are the client's default billing
                terms. A contract can override them when a
                particular work order has different commercial
                terms.
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Margin Type">
                  <Select
                    value={form.margin_type}
                    onChange={set("margin_type")}
                  >
                    {MARGIN_TYPES.map((m) => (
                      <option
                        key={m.value}
                        value={m.value}
                      >
                        {m.label}
                      </option>
                    ))}
                  </Select>
                </Field>

                <Field label="GST %">
                  <Input
                    type="number"
                    step="0.01"
                    value={form.gst}
                    onChange={set("gst")}
                  />
                </Field>

                {[
                  "fixed",
                  "fixed_plus_percentage",
                ].includes(form.margin_type) && (
                  <Field label="Fixed Margin Amount (₹)">
                    <Input
                      type="number"
                      step="0.01"
                      value={form.margin_fixed}
                      onChange={set("margin_fixed")}
                    />
                  </Field>
                )}

                {[
                  "percentage",
                  "fixed_plus_percentage",
                ].includes(form.margin_type) && (
                  <Field label="Margin %">
                    <Input
                      type="number"
                      step="0.01"
                      value={form.margin_percent}
                      onChange={set("margin_percent")}
                    />
                  </Field>
                )}

                <Field label="Payment Terms (Days)">
                  <Input
                    type="number"
                    min="0"
                    value={form.payment_terms_days}
                    onChange={set(
                      "payment_terms_days"
                    )}
                  />
                </Field>
              </div>

              <div className="mt-4 border border-slate-200 rounded-md p-4">
                <div className="nk-overline mb-2">
                  Billing Preview
                </div>

                <div className="text-sm text-slate-600">
                  Margin:{" "}
                  <span className="font-semibold text-slate-900">
                    {marginLabel(form)}
                  </span>
                  {" · "}
                  GST:{" "}
                  <span className="font-semibold text-slate-900">
                    {form.gst || 0}%
                  </span>
                  {" · "}
                  Payment:{" "}
                  <span className="font-semibold text-slate-900">
                    {form.payment_terms_days || 0} days
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-5">
            <Btn
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Btn>

            <Btn
              onClick={save}
              data-testid="save-client-btn"
            >
              Save Client
            </Btn>
          </div>
        </DialogContent>
      </Dialog>

      {contractsFor && (
        <ContractsDialog
          client={contractsFor}
          onClose={() => setContractsFor(null)}
          isSuper={isSuper}
        />
      )}

      {ledgerFor && (
        <LedgerDialog
          client={ledgerFor}
          onClose={() => setLedgerFor(null)}
        />
      )}
    </div>
  );
}

/* =====================================================
   CONTRACT COMMERCIAL OVERRIDES
===================================================== */

const C_EMPTY = {
  name: "",
  work_order_no: "",
  start_date: "",
  end_date: "",
  site: "",
  wage_type: "central",
  status: "active",

  margin_override: "inherit",
  margin_type: "none",
  margin_fixed: "0",
  margin_percent: "0",
  gst: "",
  payment_terms_days: "",
};

function ContractsDialog({
  client,
  onClose,
  isSuper,
}) {
  const [contracts, setContracts] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(C_EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () =>
    api
      .get(`/clients/${client.id}/contracts`)
      .then((r) => setContracts(r.data));

  useEffect(() => {
    load();
  }, []);

  const newContract = () => {
    setForm(C_EMPTY);
    setEditId(null);
    setAdding(true);
  };

  const editContract = (c) => {
    setForm({
      ...C_EMPTY,
      ...c,
      margin_override:
        c.margin_override ||
        (
          c.margin_type ||
          c.margin_fixed ||
          c.margin_percent
            ? "override"
            : "inherit"
        ),
    });

    setEditId(c.id);
    setAdding(true);
  };

  const save = async () => {
    try {
      if (editId) {
        await api.put(
          `/contracts/${editId}`,
          form
        );
        toast.success("Contract updated");
      } else {
        await api.post(
          `/clients/${client.id}/contracts`,
          form
        );
        toast.success("Contract added");
      }

      setAdding(false);
      setEditId(null);
      setForm(C_EMPTY);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const set = (k) => (e) =>
    setForm({
      ...form,
      [k]: e.target.value,
    });

  const contractMarginLabel = (c) => {
    if (
      !c.margin_override ||
      c.margin_override === "inherit"
    ) {
      return `Client Default (${marginLabel(client)})`;
    }

    return marginLabel(c);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Contracts — {client.name}
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-x-auto border border-slate-200 rounded-md">
          <table className="nk-table">
            <thead>
              <tr>
                <th>Contract</th>
                <th>Work Order</th>
                <th>Site</th>
                <th>Wage</th>
                <th>Margin</th>
                <th>GST %</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              {contracts.map((c) => (
                <tr key={c.id}>
                  <td className="font-medium">
                    {c.name}
                  </td>

                  <td>
                    {c.work_order_no || "—"}
                  </td>

                  <td>{c.site || "—"}</td>

                  <td className="capitalize">
                    {c.wage_type}
                  </td>

                  <td className="whitespace-nowrap">
                    {contractMarginLabel(c)}
                  </td>

                  <td className="nk-num">
                    {c.gst ||
                      client.gst ||
                      "18"}
                  </td>

                  <td>
                    <StatusBadge
                      status={c.status}
                    />
                  </td>

                  <td className="text-right">
                    {isSuper && (
                      <Btn
                        variant="ghost"
                        onClick={() =>
                          editContract(c)
                        }
                      >
                        <Pencil size={14} />
                      </Btn>
                    )}
                  </td>
                </tr>
              ))}

              {!contracts.length && (
                <tr>
                  <td
                    colSpan={8}
                    className="text-center text-slate-400 py-6"
                  >
                    No contracts
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {adding ? (
          <div className="mt-4 border-t border-slate-200 pt-4">
            <div className="font-semibold text-sm text-slate-800 mb-3">
              {editId
                ? "Edit Contract"
                : "Add Contract"}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Contract Name">
                <Input
                  value={form.name}
                  onChange={set("name")}
                  data-testid="contract-name"
                />
              </Field>

              <Field label="Work Order No">
                <Input
                  value={form.work_order_no}
                  onChange={set(
                    "work_order_no"
                  )}
                />
              </Field>

              <Field label="Start Date">
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={set("start_date")}
                />
              </Field>

              <Field label="End Date">
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={set("end_date")}
                />
              </Field>

              <Field label="Site / Location">
                <Input
                  value={form.site}
                  onChange={set("site")}
                />
              </Field>

              <Field label="Wage Type">
                <Select
                  value={form.wage_type}
                  onChange={set("wage_type")}
                >
                  <option value="central">
                    Central Wage
                  </option>

                  <option value="state">
                    State Wage
                  </option>
                </Select>
              </Field>

              <div className="col-span-2 border-t border-slate-200 pt-3 mt-1">
                <div className="nk-overline mb-3">
                  Commercial Terms
                </div>
              </div>

              <Field label="Margin Setting">
                <Select
                  value={
                    form.margin_override
                  }
                  onChange={set(
                    "margin_override"
                  )}
                >
                  <option value="inherit">
                    Inherit Client Default
                  </option>

                  <option value="override">
                    Override for this Contract
                  </option>
                </Select>
              </Field>

              <Field label="GST %">
                <Input
                  type="number"
                  step="0.01"
                  placeholder={
                    client.gst || "18"
                  }
                  value={form.gst}
                  onChange={set("gst")}
                />
              </Field>

              {form.margin_override ===
                "override" && (
                <>
                  <Field label="Margin Type">
                    <Select
                      value={
                        form.margin_type
                      }
                      onChange={set(
                        "margin_type"
                      )}
                    >
                      {MARGIN_TYPES.map(
                        (m) => (
                          <option
                            key={m.value}
                            value={m.value}
                          >
                            {m.label}
                          </option>
                        )
                      )}
                    </Select>
                  </Field>

                  <div />

                  {[
                    "fixed",
                    "fixed_plus_percentage",
                  ].includes(
                    form.margin_type
                  ) && (
                    <Field label="Fixed Margin Amount (₹)">
                      <Input
                        type="number"
                        step="0.01"
                        value={
                          form.margin_fixed
                        }
                        onChange={set(
                          "margin_fixed"
                        )}
                      />
                    </Field>
                  )}

                  {[
                    "percentage",
                    "fixed_plus_percentage",
                  ].includes(
                    form.margin_type
                  ) && (
                    <Field label="Margin %">
                      <Input
                        type="number"
                        step="0.01"
                        value={
                          form.margin_percent
                        }
                        onChange={set(
                          "margin_percent"
                        )}
                      />
                    </Field>
                  )}
                </>
              )}

              <Field label="Payment Terms (Days)">
                <Input
                  type="number"
                  min="0"
                  placeholder={
                    client.payment_terms_days ||
                    "30"
                  }
                  value={
                    form.payment_terms_days
                  }
                  onChange={set(
                    "payment_terms_days"
                  )}
                />
              </Field>

              <Field label="Status">
                <Select
                  value={form.status}
                  onChange={set("status")}
                >
                  <option value="active">
                    Active
                  </option>

                  <option value="inactive">
                    Inactive
                  </option>
                </Select>
              </Field>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <Btn
                variant="outline"
                onClick={() => {
                  setAdding(false);
                  setEditId(null);
                  setForm(C_EMPTY);
                }}
              >
                Cancel
              </Btn>

              <Btn
                onClick={save}
                data-testid="save-contract-btn"
              >
                {editId
                  ? "Update Contract"
                  : "Save Contract"}
              </Btn>
            </div>
          </div>
        ) : (
          <div className="flex justify-end mt-3">
            <Btn
              onClick={newContract}
              data-testid="add-contract-btn"
            >
              <Plus size={16} />
              Add Contract
            </Btn>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


/* =====================================================
   CLIENT LEDGER
===================================================== */

const RECEIPT_EMPTY = {
  invoice_id: "",
  receipt_date:
    new Date()
      .toISOString()
      .slice(0, 10),
  amount: "",
  payment_mode: "bank_transfer",
  reference_no: "",
  notes: "",
};


const ledgerMoney = (v) =>
  Number(v || 0).toLocaleString(
    "en-IN",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  );


function LedgerSummary({
  label,
  value,
  strong,
}) {
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white">
      <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">
        {label}
      </div>

      <div
        className={
          "mt-1 nk-num " +
          (
            strong
              ? "text-lg font-bold text-slate-950"
              : "font-semibold text-slate-800"
          )
        }
      >
        ₹{ledgerMoney(value)}
      </div>
    </div>
  );
}


function LedgerDialog({
  client,
  onClose,
}) {
  const [data, setData] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [receiptOpen, setReceiptOpen] =
    useState(false);

  const [receipt, setReceipt] =
    useState(RECEIPT_EMPTY);

  const [filters, setFilters] =
    useState({
      from_date: "",
      to_date: "",
    });


  const load = async () => {
    try {
      setLoading(true);

      const params = {};

      if (filters.from_date) {
        params.from_date =
          filters.from_date;
      }

      if (filters.to_date) {
        params.to_date =
          filters.to_date;
      }

      const r = await api.get(
        `/clients/${client.id}/ledger`,
        { params }
      );

      setData(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    load();
  }, [
    client.id,
    filters.from_date,
    filters.to_date,
  ]);


  const setReceiptField =
    (key) => (e) =>
      setReceipt({
        ...receipt,
        [key]: e.target.value,
      });


  const selectInvoice =
    (e) => {
      const invoiceId =
        e.target.value;

      const inv =
        data?.open_invoices?.find(
          (x) =>
            x.id === invoiceId
        );

      setReceipt({
        ...receipt,
        invoice_id: invoiceId,
        amount:
          inv?.balance_due || "",
      });
    };


  const saveReceipt = async () => {
    try {
      if (
        !receipt.amount ||
        Number(receipt.amount) <= 0
      ) {
        toast.error(
          "Enter a valid receipt amount"
        );
        return;
      }

      await api.post(
        `/clients/${client.id}/receipts`,
        receipt
      );

      toast.success(
        "Receipt recorded"
      );

      setReceipt(
        RECEIPT_EMPTY
      );

      setReceiptOpen(false);

      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };


  const exportLedger =
    (format) => {
      const qs =
        new URLSearchParams();

      qs.set(
        "format",
        format
      );

      if (filters.from_date) {
        qs.set(
          "from_date",
          filters.from_date
        );
      }

      if (filters.to_date) {
        qs.set(
          "to_date",
          filters.to_date
        );
      }

      downloadFile(
        `/clients/${client.id}/ledger/export?${qs.toString()}`,
        `client_ledger_${client.code || client.name}.${format}`
      );
    };


  const summary =
    data?.summary || {};


  return (
    <Dialog
      open
      onOpenChange={onClose}
    >
      <DialogContent className="max-w-7xl max-h-[94vh] overflow-y-auto">

        <DialogHeader>
          <DialogTitle>
            Client Ledger — {client.name}
          </DialogTitle>
        </DialogHeader>


        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">

          <div className="grid grid-cols-2 gap-2">
            <Field label="From Date">
              <Input
                type="date"
                value={
                  filters.from_date
                }
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    from_date:
                      e.target.value,
                  })
                }
              />
            </Field>

            <Field label="To Date">
              <Input
                type="date"
                value={
                  filters.to_date
                }
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    to_date:
                      e.target.value,
                  })
                }
              />
            </Field>
          </div>


          <div className="flex flex-wrap gap-2">

            <Btn
              variant="outline"
              onClick={() =>
                exportLedger("xlsx")
              }
            >
              <Download size={14} />
              Excel
            </Btn>

            <Btn
              variant="outline"
              onClick={() =>
                exportLedger("pdf")
              }
            >
              <Download size={14} />
              PDF
            </Btn>

            <Btn
              onClick={() =>
                setReceiptOpen(
                  !receiptOpen
                )
              }
            >
              <ReceiptText size={15} />
              Record Receipt
            </Btn>
          </div>
        </div>


        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mt-4">

          <LedgerSummary
            label="Opening Balance"
            value={
              summary.opening_balance
            }
          />

          <LedgerSummary
            label="Period Debit"
            value={
              summary.period_debit
            }
          />

          <LedgerSummary
            label="Period Credit"
            value={
              summary.period_credit
            }
          />

          <LedgerSummary
            label="Closing Balance"
            value={
              summary.closing_balance
            }
            strong
          />

          <LedgerSummary
            label="Total Invoiced"
            value={
              summary.total_invoiced
            }
          />

          <LedgerSummary
            label="Total Received"
            value={
              summary.total_received
            }
          />

          <LedgerSummary
            label="Outstanding"
            value={
              summary.outstanding
            }
            strong
          />

        </div>


        {receiptOpen && (
          <Card className="p-4 mt-4 border-blue-200 bg-blue-50/30">

            <div className="font-semibold text-sm mb-3">
              Record Client Receipt
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">

              <Field label="Against Invoice">
                <Select
                  value={
                    receipt.invoice_id
                  }
                  onChange={
                    selectInvoice
                  }
                >
                  <option value="">
                    Unallocated Receipt
                  </option>

                  {(data?.open_invoices || [])
                    .map((inv) => (
                      <option
                        key={inv.id}
                        value={inv.id}
                      >
                        {inv.number}
                        {" · Balance ₹"}
                        {ledgerMoney(
                          inv.balance_due
                        )}
                      </option>
                    ))}
                </Select>
              </Field>


              <Field label="Receipt Date">
                <Input
                  type="date"
                  value={
                    receipt.receipt_date
                  }
                  onChange={
                    setReceiptField(
                      "receipt_date"
                    )
                  }
                />
              </Field>


              <Field label="Amount (₹)">
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={
                    receipt.amount
                  }
                  onChange={
                    setReceiptField(
                      "amount"
                    )
                  }
                />
              </Field>


              <Field label="Payment Mode">
                <Select
                  value={
                    receipt.payment_mode
                  }
                  onChange={
                    setReceiptField(
                      "payment_mode"
                    )
                  }
                >
                  <option value="bank_transfer">
                    Bank Transfer
                  </option>

                  <option value="upi">
                    UPI
                  </option>

                  <option value="cheque">
                    Cheque
                  </option>

                  <option value="cash">
                    Cash
                  </option>

                  <option value="neft">
                    NEFT
                  </option>

                  <option value="rtgs">
                    RTGS
                  </option>

                  <option value="other">
                    Other
                  </option>
                </Select>
              </Field>


              <Field label="UTR / Cheque / Reference">
                <Input
                  value={
                    receipt.reference_no
                  }
                  onChange={
                    setReceiptField(
                      "reference_no"
                    )
                  }
                />
              </Field>


              <Field label="Notes">
                <Input
                  value={
                    receipt.notes
                  }
                  onChange={
                    setReceiptField(
                      "notes"
                    )
                  }
                />
              </Field>

            </div>


            <div className="flex justify-end gap-2 mt-4">

              <Btn
                variant="outline"
                onClick={() => {
                  setReceiptOpen(false);
                  setReceipt(
                    RECEIPT_EMPTY
                  );
                }}
              >
                Cancel
              </Btn>

              <Btn
                onClick={
                  saveReceipt
                }
              >
                Save Receipt
              </Btn>

            </div>

          </Card>
        )}


        <Card className="overflow-hidden mt-4">

          {loading ? (
            <div className="p-6 text-sm text-slate-500">
              Loading ledger...
            </div>
          ) : data?.rows?.length ? (

            <div className="overflow-x-auto max-h-[470px] overflow-y-auto">

              <table className="nk-table min-w-[1150px]">

                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Invoice</th>
                    <th>Reference</th>
                    <th>Description</th>
                    <th>Mode</th>
                    <th>Debit</th>
                    <th>Credit</th>
                    <th>Balance</th>
                  </tr>
                </thead>


                <tbody>
                  {data.rows.map(
                    (row, index) => (
                      <tr
                        key={
                          row.receipt_id ||
                          row.invoice_id ||
                          index
                        }
                      >
                        <td className="whitespace-nowrap">
                          {row.date || "—"}
                        </td>

                        <td>
                          <span
                            className={
                              row.type === "invoice"
                                ? "inline-flex rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-xs font-semibold text-amber-700"
                                : "inline-flex rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-xs font-semibold text-emerald-700"
                            }
                          >
                            {row.type === "invoice"
                              ? "Invoice"
                              : "Receipt"}
                          </span>
                        </td>

                        <td className="font-medium whitespace-nowrap">
                          {row.invoice_number || "—"}
                        </td>

                        <td className="whitespace-nowrap">
                          {row.reference || "—"}
                        </td>

                        <td>
                          {row.description || "—"}
                        </td>

                        <td className="capitalize">
                          {
                            row.payment_mode
                              ?.replace(
                                /_/g,
                                " "
                              ) ||
                            "—"
                          }
                        </td>

                        <td className="nk-num">
                          {Number(row.debit || 0)
                            ? `₹${ledgerMoney(row.debit)}`
                            : "—"}
                        </td>

                        <td className="nk-num text-emerald-700">
                          {Number(row.credit || 0)
                            ? `₹${ledgerMoney(row.credit)}`
                            : "—"}
                        </td>

                        <td className="nk-num font-semibold">
                          ₹{ledgerMoney(
                            row.balance
                          )}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>

              </table>

            </div>

          ) : (
            <Empty
              title="No ledger transactions"
              hint="Raised invoices and recorded receipts will appear here."
            />
          )}

        </Card>


        <div className="flex justify-end mt-4">
          <Btn
            variant="outline"
            onClick={onClose}
          >
            Close
          </Btn>
        </div>

      </DialogContent>
    </Dialog>
  );
}
